import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {ReactNode} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import preferences from 'Preferences';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Header1} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import HoverActionButton, {CLASS_NAME_FOR_HOVER_TARGET} from 'ui-library/HoverActionButton';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {BenchlingIconName} from './icons/BenchlingIcon';

const ROW_SHAPE = PropTypes.shape({});

export interface RowProps {
  id?: string;
  isArchived?: boolean;
  // Rows without an itemIndex have no attached editable item, and are not clickable
  itemIndex?: number;
  values: Array<JSX.Element | string | number | undefined>;
  isArchivable?: boolean;
  isDeletable?: boolean;
}

interface Props {
  columnHeaders: Array<{name: string; width: number}>;
  emptyMessage: ReactNode;
  leftSubheaderNode: JSX.Element | null;
  // rightSubheaderNode affect the placement of nodes passed in through the customCreateButton prop
  rightSubheaderNode?: JSX.Element | null;
  // If true, render a throbber instead of rows
  isLoading: boolean;
  // Rows can be null/undefined, empty, or have elements. Null, undefined, and empty are treated
  // equivalently, and result in emptyMessage being rendered. Otherwise, render row values.
  rows?: Array<RowProps> | null;
  onArchive?: ((itemIndex: number) => void) | null;
  onCreate?: () => void;
  onDelete?: ((itemIndex: number) => void) | null;
  onUnarchive?: ((itemIndex: number) => void) | null;
  tableName?: string;
  writable: boolean;
  getEditUrl: (itemIndex: number) => string | null;
  // Provide a custom create button to override the default button
  customCreateButton?: JSX.Element;
  // If true, the delete button will be shown along with the unarchive button for archived rows.
  allowDeleteAfterArchive?: boolean;
}

export default function EditableItemTable(props: Props): JSX.Element {
  const renderItemRow = (row: RowProps): JSX.Element => {
    const contentCol = row.values.map((value, i) => (
      <Col
        key={`row ${row.itemIndex}, col ${i}`}
        sm={props.columnHeaders[i].width}
        className="editableItemTable-content"
        dataTestAttrs={{component: 'EditableItemRow', element: 'contentCol'}}
      >
        <>{row.values[i]}</>
      </Col>
    ));
    const columns = [
      <Col key={`content, row ${row.itemIndex}`} sm={10}>
        <Row>{contentCol}</Row>
      </Col>,
      <Col key={`buttons, row ${row.itemIndex}`} sm={2} className="editableItemTable-content">
        {props.writable && (
          <ActionButtonGroup
            row={row}
            onArchive={props.onArchive}
            onDelete={props.onDelete}
            onUnarchive={props.onUnarchive}
            allowDeleteAfterArchive={props.allowDeleteAfterArchive}
          />
        )}
        {row.id !== undefined && Boolean(preferences.get('ENABLE_COPY_API_ID')) && (
          <ActionButton
            key={`copy-${row.itemIndex}`}
            icon="note-code"
            itemIndex={row.itemIndex}
            onClick={() => copyToClipboard(row.id!, {showToastOnSuccess: true})}
            tooltipTitle="Copy API ID"
            pointerClassName="u-primary-pointer"
          />
        )}
      </Col>,
    ];
    return (
      <EditableItemRow
        key={`row ${row.itemIndex}`}
        className={row.isArchived ? 'u-faded' : null}
        columns={columns}
        itemIndex={row.itemIndex!}
        getEditUrl={props.getEditUrl}
      />
    );
  };

  const renderNonItemRow = (row: RowProps, index = 0): Array<JSX.Element> =>
    row.values.map((field, i) => (
      <div key={`row ${index}, col ${i}`} className="editableItemTable-content">
        {field}
      </div>
    ));

  const contentHeaders = props.columnHeaders.map((col, i) => (
    <Col
      key={`col ${i}`}
      sm={col.width}
      className={classNames({'editableItemTable-primaryHeader': i === 0})}
      {...getTestProps({component: 'EditableItemTable', element: 'headerColumn'})}
    >
      {col.name}
    </Col>
  ));
  const headers = [
    <Col key="col content" sm={10}>
      <Row>{contentHeaders}</Row>
    </Col>,
    <Col key="col button" sm={2} />,
  ];
  const shouldShowCreate = props.writable && (props.onCreate || props.customCreateButton);
  const shouldShowHeader =
    props.tableName || shouldShowCreate || props.rightSubheaderNode || props.leftSubheaderNode;
  return (
    <div {...getTestProps({component: 'EditableItemTable', element: 'root'})}>
      {shouldShowHeader && (
        <div className="u-spacing-bottom">
          <div className="u-small-spacing-bottom">
            {props.tableName && <Header1>{props.tableName}</Header1>}
          </div>
          <div className={css(styles.subheader)}>
            {/* Render div even when leftSubheaderNode is null to preserve spacing */}
            <div>{props.leftSubheaderNode}</div>
            {shouldShowCreate &&
              (props.customCreateButton ? (
                props.customCreateButton
              ) : (
                <Button
                  buttonStyle={ButtonStyle.PRIMARY}
                  onClick={props.onCreate}
                  size={ButtonSize.SMALL}
                  text="Create"
                  dataTestAttrs={{component: 'EditableItemTable', element: 'createButton'}}
                />
              ))}
            {props.rightSubheaderNode != null && <div>{props.rightSubheaderNode}</div>}
          </div>
        </div>
      )}
      <Row className="editableItemTable-headerRow">{headers}</Row>
      {(() => {
        if (props.isLoading) {
          return renderNonItemRow({values: [<CenteredHorizontalThrobber />]});
        } else if (!props.rows || !props.rows.length) {
          return renderNonItemRow({values: [<>{props.emptyMessage}</>]});
        } else {
          return props.rows.map((row, i) =>
            row.itemIndex != null ? renderItemRow(row) : renderNonItemRow(row, i)
          );
        }
      })()}
    </div>
  );
}

export function EditableItemRow({
  className,
  columns,
  itemIndex,
  getEditUrl,
}: {
  className: string | null;
  columns: Array<JSX.Element>;
  itemIndex: number;
  getEditUrl: (itemIndex: number) => string | null;
}): JSX.Element {
  const rowUrl = getEditUrl(itemIndex);
  return rowUrl == null ? (
    <div className={css(styles.rowLink)}>
      <Row
        dataTestAttrs={{component: 'EditableItemRow', element: 'row'}}
        className={classNames(className, CLASS_NAME_FOR_HOVER_TARGET, 'editableItemTable-row')}
      >
        {columns}
      </Row>
    </div>
  ) : (
    <a
      href={rowUrl ?? undefined}
      className={css(styles.rowLink)}
      onClick={rowUrl ? BenchlingUtil.navigate : noop}
    >
      <Row
        dataTestAttrs={{component: 'EditableItemRow', element: 'row'}}
        className={classNames(className, CLASS_NAME_FOR_HOVER_TARGET, 'editableItemTable-row u-pointer')}
      >
        {columns}
      </Row>
    </a>
  );
}

export function ActionButton({
  icon,
  itemIndex,
  onClick,
  tooltipTitle,
  pointerClassName,
}: {
  icon: BenchlingIconName;
  itemIndex?: number;
  onClick: (itemIndex?: number) => void;
  tooltipTitle: string;
  pointerClassName: string;
}): JSX.Element {
  return (
    <Tooltip text={tooltipTitle} disableContentSelection={true}>
      <HoverActionButton
        icon={icon}
        className={classNames(
          'faded u-small-padding-left u-small-padding-right pull-right',
          pointerClassName
        )}
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          onClick(itemIndex);
        }}
      />
    </Tooltip>
  );
}

function ActionButtonGroup({
  row,
  onArchive,
  onDelete,
  onUnarchive,
  allowDeleteAfterArchive,
}: {
  row: RowProps;
  onArchive?: ((itemIndex: number) => void) | null;
  onDelete?: ((itemIndex: number) => void) | null;
  onUnarchive?: ((itemIndex: number) => void) | null;
  allowDeleteAfterArchive?: boolean;
}): JSX.Element | null {
  let unarchiveButton = null;
  let archiveButton = null;
  let deleteButton = null;
  if (BenchlingConfig.ENABLE_ARCHIVING_V2 && onUnarchive && row.isArchived) {
    unarchiveButton = (
      <ActionButton
        key={`unarchive-${row.itemIndex}`}
        icon="unarchive"
        itemIndex={row.itemIndex}
        onClick={onUnarchive}
        tooltipTitle="Unarchive"
        pointerClassName="u-danger-pointer"
      />
    );
  } else if (BenchlingConfig.ENABLE_ARCHIVING_V2 && onArchive && row.isArchivable !== false) {
    archiveButton = (
      <ActionButton
        key={`archive-${row.itemIndex}`}
        icon="archive"
        itemIndex={row.itemIndex}
        onClick={onArchive}
        tooltipTitle="Archive"
        pointerClassName="u-danger-pointer"
      />
    );
  }
  if (onDelete && (row.isDeletable === undefined || row.isDeletable === true)) {
    deleteButton = (
      <ActionButton
        key={`delete-${row.itemIndex}`}
        icon="format-delete"
        itemIndex={row.itemIndex}
        onClick={onDelete}
        tooltipTitle="Delete"
        pointerClassName="u-danger-pointer"
      />
    );
  }
  if (allowDeleteAfterArchive) {
    return (
      archiveButton || (
        <>
          {unarchiveButton}
          {deleteButton}
        </>
      )
    );
  } else {
    return archiveButton || unarchiveButton || deleteButton;
  }
}

ActionButton.propTypes = {
  icon: PropTypes.string.isRequired,
  itemIndex: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  tooltipTitle: PropTypes.string.isRequired,
  pointerClassName: PropTypes.string.isRequired,
};

ActionButtonGroup.propTypes = {
  onArchive: PropTypes.func,
  onDelete: PropTypes.func,
  onUnarchive: PropTypes.func,
  deleteAfterArchive: PropTypes.bool,
  row: ROW_SHAPE.isRequired,
};

const styles = StyleSheet.create({
  rowLink: {
    display: 'block',
    color: 'inherit',
    textDecoration: 'inherit',
  },
  subheader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
});
